import { useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { Mail, Plus, Save, Trash2 } from "lucide-react";
import { GmailSetup } from "@/components/settings/GmailSetup";

const inputCls = "w-full border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";
const labelCls = "text-[10px] uppercase tracking-[0.2em] font-bold text-[#15233B]/60";
const PLACEHOLDERS = ["{first_name}", "{full_name}", "{my_name}", "{position}"];

function TemplateCard({ tpl, onSaved, onDeleted }) {
  const [f, setF] = useState({ name: tpl.name || "", subject: tpl.subject || "", body: tpl.body || "" });
  const [busy, setBusy] = useState(false);
  const isNew = !tpl.id;
  const dirty = isNew || f.name !== tpl.name || f.subject !== tpl.subject || f.body !== tpl.body;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const tid = tpl.id || "new";

  const save = async () => {
    setBusy(true);
    try {
      const { data } = isNew
        ? await api.post("/email-templates", { workspace: tpl.workspace, ...f })
        : await api.patch(`/email-templates/${tpl.id}`, f);
      toast.success("Template saved");
      onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (isNew) return onDeleted();
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try { await api.delete(`/email-templates/${tpl.id}`); toast.success("Template deleted"); onDeleted(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid={`email-template-${tid}`} className="bg-white border border-[#15233B]/10 rounded-md p-4 space-y-2.5">
      <div>
        <label className={labelCls}>Template name</label>
        <input data-testid={`email-template-name-${tid}`} value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g., Interview invitation" className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Subject</label>
        <input data-testid={`email-template-subject-${tid}`} value={f.subject} onChange={(e) => set("subject", e.target.value)} className={inputCls} />
      </div>
      <div>
        <label className={labelCls}>Body</label>
        <textarea data-testid={`email-template-body-${tid}`} rows={7} value={f.body} onChange={(e) => set("body", e.target.value)} className={inputCls} />
      </div>
      <div className="flex items-center gap-2">
        <button data-testid={`email-template-save-${tid}`} onClick={save} disabled={busy || !dirty}
          className="flex items-center gap-1 px-3 py-1.5 rounded bg-[#1E73C9] text-white text-xs font-bold hover:bg-[#15233B] disabled:opacity-40">
          <Save className="h-3.5 w-3.5" /> {busy ? "Saving…" : "Save"}
        </button>
        <button data-testid={`email-template-delete-${tid}`} onClick={remove} className="ml-auto flex items-center gap-1 text-xs font-semibold text-[#15233B]/50 hover:text-[#b91c1c]">
          <Trash2 className="h-3.5 w-3.5" /> {isNew ? "Discard" : "Delete"}
        </button>
      </div>
    </div>
  );
}

export function EmailTemplatesAdmin() {
  const [ws, setWs] = useState("intake");
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    setDraft(null);
    api.get("/email-templates", { params: { workspace: ws } }).then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, [ws]);

  return (
    <div data-testid="email-templates-admin" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-sm font-extrabold tracking-[0.15em] uppercase text-[#15233B] flex items-center gap-2"><Mail className="h-4 w-4 text-[#1E73C9]" /> Email templates</h3>
        <div className="flex rounded-full border border-[#15233B]/15 overflow-hidden">
          {[["intake", "Clients"], ["recruiting", "Recruiting"]].map(([k, l]) => (
            <button key={k} data-testid={`email-templates-ws-${k}`} onClick={() => setWs(k)}
              className={`px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.12em] transition-colors ${ws === k ? "bg-[#FFCB05] text-[#15233B]" : "bg-white text-[#15233B]/55 hover:text-[#15233B]"}`}>{l}</button>
          ))}
        </div>
        <button data-testid="email-template-add" onClick={() => setDraft({ workspace: ws, name: "", subject: "", body: "" })} disabled={!!draft}
          className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#FFCB05] text-[#15233B] text-xs font-extrabold hover:bg-[#FBC02D] disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" /> New template
        </button>
      </div>
      <GmailSetup />
      <p className="text-xs text-[#15233B]/60">
        Staff pick these in the &ldquo;Send email&rdquo; box on any lead. Placeholders filled in automatically: {PLACEHOLDERS.map((p) => <code key={p} className="mx-0.5 px-1 rounded bg-[#15233B]/5">{p}</code>)}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {draft && <TemplateCard key="new" tpl={draft} onSaved={(d) => { setItems((x) => [...x, d]); setDraft(null); }} onDeleted={() => setDraft(null)} />}
        {items.map((t) => (
          <TemplateCard key={t.id} tpl={t} onSaved={(d) => setItems((x) => x.map((i) => (i.id === d.id ? d : i)))} onDeleted={() => setItems((x) => x.filter((i) => i.id !== t.id))} />
        ))}
        {items.length === 0 && !draft && <div data-testid="email-templates-empty" className="text-sm text-[#15233B]/50 italic">No templates for this workspace yet.</div>}
      </div>
    </div>
  );
}
